import React, {useState} from 'react';

import {AnalysisGraphQL} from 'analysis-tool/AnalysisTypes';
import {StepDataFrameLineageNode} from 'analysis-tool/data/DatasetLineageUtils';
import {
  getDefaultDataFrameColumnMapping,
  isDataFrameColumnMappingComplete,
} from 'analysis-tool/data-frame-creation/column-mapping/ColumnMappingUtil';
import AnalysisTableSelector from 'analysis-tool/ui/components/AnalysisTableSelector';
import {assert} from 'Asserts';
import getUserColumns from 'data-frames/util/getUserColumns';
import Modal from 'ui-library/modals/Modal';

import {
  getInputTableApplyStateName,
  InputTableApplyState,
  InputTableLineageNode,
} from './AnalysisTemplateUtil';
import AnalysisTemplateInputTableColumnMapper from './table-mapping/AnalysisTemplateInputTableColumnMapper';

export interface AnalysisTemplateTableSelectorModalProps {
  analysis: AnalysisGraphQL;
  inputTable: InputTableApplyState;
  onRequestSubmit: (result: {
    lineageNode: InputTableLineageNode;
    columnMapping: {[templateColumn: string]: string};
  }) => void;
  onRequestClose: () => void;
  getInputTableDisabledMessage?: (
    analysis: AnalysisGraphQL,
    inputLineageNode: StepDataFrameLineageNode,
    selectedLineageNode: StepDataFrameLineageNode | null
  ) => string | null;
}

export default function AnalysisTemplateTableSelectorModal({
  analysis,
  inputTable,
  onRequestSubmit,
  onRequestClose,
  getInputTableDisabledMessage,
}: AnalysisTemplateTableSelectorModalProps): JSX.Element {
  const name = getInputTableApplyStateName(inputTable);
  const [selectedLineageNode, setSelectedLineageNode] = useState<InputTableLineageNode | null>(
    inputTable.lineageNode
  );

  const templateDataFrame =
    inputTable.templateLineageNode != null
      ? inputTable.templateLineageNode.data.dataFrame
      : inputTable.dataFrameColumns
        ? {
            __typename: 'DataFrameNode' as const,
            allColumnsJson: inputTable.dataFrameColumns,
            name,
          }
        : null;
  assert(templateDataFrame != null, 'Expected either templateLineageNode or dataFrameColumns to be defined');

  const templateDataFrameColumns = getUserColumns({
    columns: templateDataFrame.allColumnsJson,
  });

  const selectedDataFrame = selectedLineageNode?.data.dataFrame;

  const initialColumnMapping =
    inputTable.columnMapping ??
    getDefaultDataFrameColumnMapping(templateDataFrameColumns, selectedDataFrame?.allColumnsJson ?? []);

  const [columnMapping, setColumnMapping] = useState<Record<string, string>>(initialColumnMapping);

  const analysisTableSelector = (
    <AnalysisTableSelector
      analysis={analysis}
      selectedLineageNode={selectedLineageNode}
      onSelect={(lineageNode: InputTableLineageNode) => {
        setSelectedLineageNode(lineageNode);
        const selectedDataFrame = lineageNode?.data.dataFrame;
        const defaultColumnMapping = getDefaultDataFrameColumnMapping(
          templateDataFrameColumns,
          selectedDataFrame?.allColumnsJson ?? []
        );
        setColumnMapping(defaultColumnMapping);
      }}
      includeViews={false}
      getDisabledMessage={(inputLineageNode: StepDataFrameLineageNode) =>
        getInputTableDisabledMessage?.(analysis, inputLineageNode, inputTable.lineageNode) ?? null
      }
      dataTestAttrs={{
        component: 'AnalysisTemplateTableSelectorModal',
        element: 'analysisTableSelector',
      }}
    />
  );

  return (
    <Modal
      fullWidth={true}
      bodyPadded={false}
      expandHeight={true}
      title={`Select table for ${name}`}
      onRequestSubmit={() => {
        onRequestSubmit({
          lineageNode: selectedLineageNode!,
          columnMapping,
        });
      }}
      onRequestClose={onRequestClose}
      buttons={[
        {
          cancel: true,
          text: 'Cancel',
        },
        {
          submit: true,
          text: 'Save',
          disabled:
            selectedLineageNode == null ||
            !isDataFrameColumnMappingComplete(templateDataFrameColumns, columnMapping),
        },
      ]}
    >
      <AnalysisTemplateInputTableColumnMapper
        columnMapping={columnMapping}
        setColumnMapping={setColumnMapping}
        inputTableColumns={selectedLineageNode?.data.dataFrame.allColumnsJson ?? null}
        inputTableDataFrameIdForPreview={selectedLineageNode?.data.dataFrame.id ?? null}
        inputTableSelector={analysisTableSelector}
        inputTableSelectorLabel="Analysis table"
        templateTableDataFrame={templateDataFrame}
        templateTableLabel="Starting table"
        templateTableDescription={inputTable.description}
        instructions="Select an existing table to use in place of this variable starting table."
      />
    </Modal>
  );
}
